import { useCallback } from "react";
import { Document } from "models/schemas";
import { FileMetadata } from "components/input-files/definitions";
import {
  getFileExtension,
  getFileNameWithoutExtension,
} from "utils/date-format";
import { StagedFile, WizardFileOperationsProps } from "./definitions";
import {
  getAllVersionsForFile,
  createStagedFileFromDocument,
} from "./version-utils";
import {
  processNewFileUploads,
  processPendingVersions,
  processRenamedFiles,
  processFileDeletions,
} from "./utils";

export const useWizardFileOperations = (props: WizardFileOperationsProps) => {
  const {
    uploadUrlCall,
    registerDocCall,
    deleteDocCall,
    updateDocCall,
    lazyDownloadUrlCall,
    addNotification,
    teamName,
    currentUser,
    parentId,
    category,
    stagedFiles,
    onStagedFilesChange,
    isCreateMode,
  } = props;

  const downloadSingleDoc = useCallback(
    async (stagedFile: StagedFile) => {
      try {
        if (stagedFile.originalDocument?.s3Key) {
          const request = {
            documentId: stagedFile.originalDocument?.documentId,
            preview: false,
          };
          const response = await lazyDownloadUrlCall(request).unwrap();

          if (response?.url) {
            window.location.href = response.url;
          }
        }
      } catch (_e) {
        addNotification({
          type: "error",
          header: "File download failed.",
          dismissible: true,
          timeout: 5000,
        });
      }
    },
    [lazyDownloadUrlCall, addNotification],
  );

  const downloadDocs = useCallback(
    async (selectedFiles: StagedFile[]) => {
      for (const stagedFile of selectedFiles) {
        if (stagedFile.originalDocument) {
          await downloadSingleDoc(stagedFile);
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }
    },
    [downloadSingleDoc],
  );

  const addStagedFile = useCallback(
    (file: File, fileName: string, documentType: string) => {
      const newStagedFile: StagedFile = {
        id: crypto.randomUUID(),
        file,
        fileName,
        documentType,
        isNew: true,
      };
      onStagedFilesChange([...stagedFiles, newStagedFile]);
    },
    [stagedFiles, onStagedFilesChange],
  );

  const addStagedLink = useCallback(
    (link: string, fileName: string, documentType: string) => {
      const newStagedFile: StagedFile = {
        id: crypto.randomUUID(),
        link,
        fileName,
        documentType,
        isNew: true,
      };
      onStagedFilesChange([...stagedFiles, newStagedFile]);
    },
    [stagedFiles, onStagedFilesChange],
  );

  const addMultipleStagedFiles = useCallback(
    (files: FileMetadata[]) => {
      const updatedStagedFiles = [...stagedFiles];

      for (const fileMetadata of files) {
        const extension = getFileExtension(fileMetadata.file.name);
        // Check if this file matches an existing (non-new, non-deleted) staged file
        const existingIndex = updatedStagedFiles.findIndex(
          (sf) =>
            !sf.isNew &&
            !sf.toDelete &&
            sf.originalDocument &&
            sf.originalDocument.extensionType === extension &&
            sf.originalDocument.fileName ===
              getFileNameWithoutExtension(fileMetadata.fileName),
        );

        if (existingIndex >= 0) {
          // Version collision — add as pending version
          const existing = updatedStagedFiles[existingIndex];
          const pendingVersion = {
            file: fileMetadata.file,
            fileName: fileMetadata.fileName,
            documentType: fileMetadata.documentType,
          };
          updatedStagedFiles[existingIndex] = {
            ...existing,
            pendingVersions: [
              ...(existing.pendingVersions || []),
              pendingVersion,
            ],
          };
        } else {
          // New file
          updatedStagedFiles.push({
            id: crypto.randomUUID(),
            file: fileMetadata.file,
            fileName: fileMetadata.fileName,
            documentType: fileMetadata.documentType,
            isNew: true,
          });
        }
      }

      onStagedFilesChange(updatedStagedFiles);
    },
    [stagedFiles, onStagedFilesChange],
  );

  const removeStagedFile = useCallback(
    (fileId: string) => {
      const fileToRemove = stagedFiles.find((f) => f.id === fileId);
      if (fileToRemove && !fileToRemove.isNew) {
        // Mark existing file for deletion instead of removing it
        const updatedFiles = stagedFiles.map((f) =>
          f.id === fileId ? { ...f, toDelete: true } : f,
        );
        onStagedFilesChange(updatedFiles);
      } else {
        // Remove new staged file completely
        const updatedFiles = stagedFiles.filter((f) => f.id !== fileId);
        onStagedFilesChange(updatedFiles);
      }
    },
    [stagedFiles, onStagedFilesChange],
  );

  const updateStagedFileName = useCallback(
    (fileId: string, newFileName: string) => {
      const fileToRename = stagedFiles.find((f) => f.id === fileId);
      if (!fileToRename) {
        return;
      }

      const allVersions = getAllVersionsForFile(fileToRename);
      const versionIds = new Set(allVersions.map((v) => v.documentId));

      const updatedFiles = stagedFiles.map((f) => {
        if (
          f.id === fileId ||
          (allVersions.length > 1 && versionIds.has(f.id))
        ) {
          // Also update fileName in pendingVersions if they exist
          const updatedPendingVersions = f.pendingVersions?.map((pv) => ({
            ...pv,
            fileName: newFileName,
          }));

          return {
            ...f,
            fileName: newFileName,
            ...(updatedPendingVersions && {
              pendingVersions: updatedPendingVersions,
            }),
          };
        }
        return f;
      });

      onStagedFilesChange(updatedFiles);
    },
    [stagedFiles, onStagedFilesChange],
  );

  const restoreStagedFile = useCallback(
    (fileId: string) => {
      const updatedFiles = stagedFiles.map((f) =>
        f.id === fileId ? { ...f, toDelete: false } : f,
      );
      onStagedFilesChange(updatedFiles);
    },
    [stagedFiles, onStagedFilesChange],
  );

  // Function to process all staged files after successful wizard submission
  const processStagedFiles = useCallback(
    async (actualParentId: string, gaiaObjectIdOverride?: string) => {
      // props.gaiaObjectId is used when there exists a valid Id at the time of mounting useWizardFileOperations
      // Ex: an assessment with a parent noticeId
      // gaiaObjectIdOverride is passed to processStagedFiles directly in cases where useWizardFileOperations is mounted before
      // there is a valid Id. Ex adding files when creating an event
      const effectiveGaiaObjectId = gaiaObjectIdOverride || props.gaiaObjectId;
      try {
        // Process new file uploads
        const uploadResults = await processNewFileUploads(
          stagedFiles,
          actualParentId,
          effectiveGaiaObjectId,
          category,
          teamName,
          uploadUrlCall,
          registerDocCall,
        );

        // Process pending versions
        const versionResults = await processPendingVersions(
          stagedFiles,
          actualParentId,
          effectiveGaiaObjectId,
          category,
          teamName,
          uploadUrlCall,
          registerDocCall,
        );

        // Process renamed files
        const renameResults = await processRenamedFiles(
          stagedFiles,
          updateDocCall,
        );

        // Process deletions
        const deleteResults = await processFileDeletions(
          stagedFiles,
          deleteDocCall,
        );

        // Aggregate results
        const totalUploaded =
          uploadResults.uploaded +
          versionResults.uploaded +
          renameResults.updated;
        const totalErrors =
          uploadResults.errors +
          versionResults.errors +
          renameResults.errors +
          deleteResults.errors;

        // Show notifications
        const newFileCount = stagedFiles.filter(
          (f) => f.isNew && !f.toDelete,
        ).length;
        const renamedFileCount = stagedFiles.filter(
          (f) =>
            !f.isNew &&
            !f.toDelete &&
            f.originalDocument &&
            f.fileName !==
              getFileNameWithoutExtension(f.originalDocument.fileName),
        ).length;

        if (newFileCount > 0) {
          addNotification({
            type: "success",
            header: `${newFileCount} file${
              newFileCount > 1 ? "s" : ""
            } uploaded successfully.`,
            dismissible: true,
            timeout: 5000,
          });
        }

        if (renamedFileCount > 0) {
          addNotification({
            type: "success",
            header: `${renamedFileCount} file${
              renamedFileCount > 1 ? "s" : ""
            } renamed successfully.`,
            dismissible: true,
            timeout: 5000,
          });
        }

        if (deleteResults.deleted > 0) {
          addNotification({
            type: "success",
            header: `${deleteResults.deleted} file${
              deleteResults.deleted > 1 ? "s" : ""
            } deleted successfully.`,
            dismissible: true,
            timeout: 5000,
          });
        }

        if (totalErrors > 0) {
          addNotification({
            type: "error",
            header: `${totalErrors} file operation${
              totalErrors > 1 ? "s" : ""
            } failed.`,
            dismissible: true,
            timeout: 5000,
          });
        }

        return {
          uploaded: totalUploaded,
          deleted: deleteResults.deleted,
          errors: totalErrors,
        };
      } catch (_e) {
        addNotification({
          type: "error",
          header: "File operations failed.",
          dismissible: true,
          timeout: 5000,
        });
        return { uploaded: 0, deleted: 0, errors: stagedFiles.length };
      }
    },
    [
      stagedFiles,
      uploadUrlCall,
      registerDocCall,
      deleteDocCall,
      updateDocCall,
      teamName,
      category,
      props.gaiaObjectId,
      addNotification,
    ],
  );

  // Function to load existing files for edit mode
  const loadExistingFiles = useCallback(
    (documents: Document[]) => {
      const existingFiles: StagedFile[] = documents.map((doc: Document) =>
        createStagedFileFromDocument(doc),
      );

      onStagedFilesChange(existingFiles);
    },
    [onStagedFilesChange],
  );

  return {
    downloadSingleDoc,
    downloadDocs,
    addStagedFile,
    addStagedLink,
    addMultipleStagedFiles,
    removeStagedFile,
    updateStagedFileName,
    restoreStagedFile,
    processStagedFiles,
    loadExistingFiles,
  };
};
